import React, { useEffect, useState } from 'react';
import { PageHeader, PageLoader } from '@/components/common/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { KpiCard } from '@/components/common/KpiCard';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { CheckCircle2, RotateCcw } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';

export default function SystemHealthPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  usePageTitle('System Health');

  const load = () => api.get('/admin/health').then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const resetDemo = async () => {
    setBusy(true);
    try {
      await api.post('/admin/demo/reset');
      toast.success('Demonstration environment restored');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally { setBusy(false); }
  };

  if (!data) return <div className="p-6"><PageLoader /></div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader title="Operational health" subtitle="Cross-tenant view of platform vitals."
        actions={
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="demo-reset-btn"><RotateCcw size={14} className="mr-1" /> Reset demo environment</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Restore demonstration environment?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will re-seed the primary demonstration organisation, users, asset, zones, observations and scenario data.
                  Production and non-demonstration data will not be affected. Continue?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={resetDemo} disabled={busy} data-testid="demo-reset-confirm">{busy ? 'Resetting…' : 'Reset demo'}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Organisations" value={data.organizations} />
        <KpiCard label="Active orgs" value={data.active_organizations} tone="ok" />
        <KpiCard label="Users" value={data.users} />
        <KpiCard label="Assets" value={data.assets} />
        <KpiCard label="Observations" value={data.observations} />
        <KpiCard label="Detections" value={data.detections} />
        <KpiCard label="Alerts" value={data.alerts} tone="warn" />
        <KpiCard label="Incidents" value={data.incidents} tone="info" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <Card className="p-4">
          <div className="text-sm font-semibold">Data sources</div>
          <div className="mt-3 divide-y divide-border">
            {data.data_sources.map(s => (
              <div key={s.name} className="flex items-center gap-3 py-2 text-sm">
                <CheckCircle2 size={14} className="text-emerald-500" />
                <div className="flex-1">{s.name}</div>
                <Badge variant="outline" className="text-[10px]">{s.kind}</Badge>
                <Badge className="text-[10px]">{s.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm font-semibold">Model versions</div>
          <div className="mt-3 divide-y divide-border">
            {data.model_versions.map(m => (
              <div key={m.name} className="flex items-center gap-3 py-2 text-sm">
                <CheckCircle2 size={14} className="text-emerald-500" />
                <div className="flex-1 font-mono">{m.name}</div>
                <Badge variant="outline" className="text-[10px]">v{m.version}</Badge>
                <Badge className="text-[10px]">{m.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
